import api from './Api';

export interface Transaction {
  id: number;
  product: {
    id: number;
    name: string;
    firm?: string;
    model?: string;
    size?: string;
    // E-commerce cost fields
    acquisitionCost?: number;
    deliveryCost?: number;
    adsCost?: number;
    otherCosts?: number;
  };
  company?: {
    id: number;
    name: string;
  };
  deposit?: {
    id: number;
    name: string;
    location?: string;
  };
  user: {
    id: number;
    username: string;
  };
  transactionType: string;
  quantity: number;
  unitPrice?: number;
  totalAmount?: number;
  transactionDate: string;
  notes?: string;
  // E-commerce transaction fields
  deliveryCost?: number;
  adsCost?: number;
  platformFee?: number;
  paymentProcessingFee?: number;
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
  shippingAddress?: string;
  trackingNumber?: string;
  orderStatus?: string;
}

export interface SaleRequest {
  companyId?: number;
  depositId?: number;
  quantity: number;
  unitPrice: number;
  notes?: string;
}

export interface StockRequest {
  companyId?: number;
  depositId?: number;
  quantity: number;
  notes?: string;
}

export const TransactionApi = {
  // Get all transactions for a company with optional filters
  getAllTransactions: (companyId: number, filters?: {
    startDate?: string;
    endDate?: string;
    transactionType?: string;
  }) => {
    const params = new URLSearchParams({ companyId: companyId.toString() });
    
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.transactionType) params.append('transactionType', filters.transactionType);
    
    return api.get(`/products/transactions?${params.toString()}`);
  },

  // Get all transactions for a deposit with optional filters
  getAllTransactionsByDeposit: (depositId: number, filters?: {
    startDate?: string;
    endDate?: string;
    transactionType?: string;
  }) => {
    const params = new URLSearchParams({ depositId: depositId.toString() });
    
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.transactionType) params.append('transactionType', filters.transactionType);
    
    return api.get(`/products/transactions/by-deposit?${params.toString()}`);
  },

  // Get sales history for a deposit
  getSalesHistoryByDeposit: (depositId: number) => {
    return api.get(`/products/sales-history/by-deposit?depositId=${depositId}`);
  },

  // Get transaction history for a specific product
  getProductHistory: (productId: number, companyId: number) => {
    return api.get(`/products/${productId}/history?companyId=${companyId}`);
  },

  // Get transaction history for a specific product in a deposit
  getProductHistoryByDeposit: (productId: number, depositId: number) => {
    return api.get(`/products/${productId}/history/by-deposit?depositId=${depositId}`);
  },

  // Get sales history for a company
  getSalesHistory: (companyId: number) => {
    return api.get(`/products/sales-history?companyId=${companyId}`);
  },

  // Record a sale
  recordSale: (productId: number, saleData: SaleRequest) => {
    return api.post(`/products/${productId}/sell`, saleData);
  },

  // Add stock to inventory
  addStock: (productId: number, stockData: StockRequest) => {
    return api.post(`/products/${productId}/add-stock`, stockData);
  },

  // Remove stock from inventory
  removeStock: (productId: number, stockData: StockRequest) => {
    return api.post(`/products/${productId}/remove-stock`, stockData);
  },

  // Record stock movement into deposit (STOCK_IN)
  recordStockIn: (productId: number, stockData: StockRequest) => {
    return api.post(`/products/${productId}/stock-in`, stockData);
  },

  // Record stock movement out of deposit (STOCK_OUT)
  recordStockOut: (productId: number, stockData: StockRequest) => {
    return api.post(`/products/${productId}/stock-out`, stockData);
  }
};
